import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgIf } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { MemberUserSearchResult } from '../../../../models/users/dto/member-user-search-result';
import { MemberUser } from '../../../../models/users/dto/member-user';
import { PillItem } from '../../../../models/shared/pill-item';
import { MemberSearchInputComponent } from '../member-search-input/member-search-input.component';
import { PillComponent } from '../../../../ui/pills/pill/pill.component';

@Component({
  selector: 'app-member-search',
  templateUrl: './member-search.component.html',
  styleUrls: ['./member-search.component.scss'],
  standalone: true,
  imports: [AsyncPipe, NgIf, ReactiveFormsModule, MemberSearchInputComponent, PillComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MemberSearchComponent implements OnChanges {
  @Input() employerId?: number;
  @Input() disabled = false;
  @Input() label = $localize`Member Search`;
  @Input() placeHolder = $localize`Search by name or member ID`;
  @Input() searchByNameOnly = false;
  @Input() selectedMember: MemberUser | null = null;
  @Output() selectedMemberIdChange = new EventEmitter<number | undefined>();

  public readonly memberControl = new FormControl<MemberUserSearchResult | null>(null);

  private _selectedMember = new BehaviorSubject<MemberUserSearchResult | null>(null);
  public selectedMember$ = this._selectedMember.asObservable();

  public selectedMemberPillItem$ = this.selectedMember$.pipe(
    filter((member): member is MemberUserSearchResult => member !== null),
    map(member => new PillItem(`${member.name} (${member.id})`, true))
  );

  constructor() {
    this.memberControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(member => {
      this._selectedMember.next(member);
      this.selectedMemberIdChange.emit(member?.id ?? undefined);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this.disabled
        ? this.memberControl.disable({ emitEvent: false })
        : this.memberControl.enable({ emitEvent: false });
    }
    if (changes.selectedMember && this.selectedMember) {
      this.memberControl.setValue(
        new MemberUserSearchResult(
          this.selectedMember.id,
          this.selectedMember.fullName,
          this.selectedMember.firstName,
          this.selectedMember.lastName
        )
      );
    }
  }

  public clearSelectedMember(): void {
    this.memberControl.reset(null, { emitEvent: false });
    this._selectedMember.next(null);
    this.selectedMemberIdChange.emit(undefined);
  }
}
